/**
 * Authoritative Chart of Accounts (COA) Mappings for VSB Farm Management
 * Source of Truth: Locked Project Documentation and Authoritative Workbook Definitions
 */

export interface ChartOfAccountItem {
  code: string;
  name: string;
  category: string;
  type:
    | 'revenue'
    | 'piglet_cost'
    | 'feed_cost'
    | 'med_cost'
    | 'expense'
    | 'adjustment_deduction'
    | 'adjustment_credit'
    | 'other_income';
}

export const CHART_OF_ACCOUNTS: Record<string, ChartOfAccountItem> = {
  '1010': { code: '1010', name: 'Pig Sales', category: 'Sales', type: 'revenue' },
  '1011': { code: '1011', name: 'Pig Sales - Others', category: 'Sales', type: 'revenue' },
  '2010': { code: '2010', name: 'Mortality', category: 'Mortality', type: 'expense' },
  '3010': { code: '3010', name: 'Piglets', category: 'Piglets', type: 'piglet_cost' },
  '3012': { code: '3012', name: 'Piglets - Trucking', category: 'Piglets', type: 'piglet_cost' },
  '3015': { code: '3015', name: 'Piglets - Kg Over', category: 'Piglets', type: 'piglet_cost' },
  '3020': { code: '3020', name: 'Feeds', category: 'Feeds', type: 'feed_cost' },
  '3021': { code: '3021', name: 'Feeds Mark-up', category: 'Feeds', type: 'feed_cost' },
  '3030': { code: '3030', name: 'Meds - VSB', category: 'Medicines', type: 'med_cost' },
  '3031': { code: '3031', name: 'Meds - SIDC', category: 'Medicines', type: 'med_cost' },
  '3032': { code: '3032', name: 'Meds - Others', category: 'Medicines', type: 'med_cost' },
  '3033': { code: '3033', name: 'ASF Test', category: 'Medicines', type: 'med_cost' },
  '3034': { code: '3034', name: 'ASF Vaccine', category: 'Medicines', type: 'med_cost' },
  '3040': { code: '3040', name: 'Trucking - Feeds', category: 'Farm Expenses', type: 'expense' },
  '3050': { code: '3050', name: 'Management', category: 'Farm Expenses', type: 'expense' },
  '3051': { code: '3051', name: 'Pig Hauling Permit', category: 'Farm Expenses', type: 'expense' },
  '4010': { code: '4010', name: 'Retail Sales Raiser', category: 'Settlement Adjustments', type: 'adjustment_deduction' },
  '4020': { code: '4020', name: 'Used Sacks', category: 'Settlement Adjustments', type: 'adjustment_credit' },
  '4030': { code: '4030', name: 'Cash Advance', category: 'Settlement Adjustments', type: 'adjustment_deduction' },
  '4031': { code: '4031', name: 'Cash Advance - Feeds', category: 'Settlement Adjustments', type: 'adjustment_deduction' },
  '4040': { code: '4040', name: 'Biogas', category: 'Settlement Adjustments', type: 'adjustment_deduction' },
  '4041': { code: '4041', name: 'Biogas Maintenance', category: 'Settlement Adjustments', type: 'adjustment_deduction' },
  '4050': { code: '4050', name: "Raiser's Expense", category: 'Settlement Adjustments', type: 'adjustment_deduction' },
};

/**
 * Returns the human-readable authoritative account label for an account code.
 */
export function getAccountName(code?: string | null): string {
  if (!code) return 'Unassigned Account';
  return CHART_OF_ACCOUNTS[code]?.name || `Account ${code}`;
}

/**
 * Returns all expense-eligible account codes (3012, 3015, 3040, 3050, 3051).
 */
export const EXPENSE_ACCOUNT_OPTIONS = [
  { code: '3050', label: '3050 — Management' },
  { code: '3051', label: '3051 — Pig Hauling Permit' },
  { code: '3012', label: '3012 — Piglets - Trucking' },
  { code: '3015', label: '3015 — Piglets - Kg Over' },
  { code: '3040', label: '3040 — Trucking - Feeds' },
];

/**
 * Returns all medicine-eligible account codes (3030, 3031, 3032, 3033, 3034).
 */
export const MEDICINE_ACCOUNT_OPTIONS = [
  { code: '3030', label: '3030 — Meds - VSB' },
  { code: '3031', label: '3031 — Meds - SIDC' },
  { code: '3032', label: '3032 — Meds - Others' },
  { code: '3033', label: '3033 — ASF Test' },
  { code: '3034', label: '3034 — ASF Vaccine' },
];

/**
 * Returns all settlement adjustment account codes (4010, 4020, 4030, 4031, 4040, 4041, 4050).
 */
export const ADJUSTMENT_ACCOUNT_OPTIONS = [
  { code: '4010', label: '4010 — Retail Sales Raiser', type: 'deduction' },
  { code: '4020', label: '4020 — Used Sacks (Credit)', type: 'addition' },
  { code: '4030', label: '4030 — Cash Advance', type: 'deduction' },
  { code: '4031', label: '4031 — Cash Advance - Feeds', type: 'deduction' },
  { code: '4040', label: '4040 — Biogas', type: 'deduction' },
  { code: '4041', label: '4041 — Biogas Maintenance', type: 'deduction' },
  { code: '4050', label: "4050 — Raiser's Expense", type: 'deduction' },
];
